import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllEmployees,
  getProjects,
} from "../redux/actionCreator";
import {
  all_employees,
  employees_page_details,
  project_data,
} from "../redux/commonReducer";

export default function Dashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const employees = useSelector(all_employees) || [];
  const empPage = useSelector(employees_page_details);
  const projects = useSelector(project_data) || [];

  useEffect(() => {
    dispatch(getAllEmployees({ page: 0, limit: 10 }));
    dispatch(getProjects({ page: 0, limit: 100 }));
  }, []);

  const stats = useMemo(() => {
    const totalEmp = empPage?.totalCount ?? employees.length;
    const approved = employees.filter((e) => e.status === "approved").length;
    const blocked = employees.filter((e) => e.status === "blocked").length;
    const pending = employees.filter((e) => e.status === "pending").length;
    return {
      totalEmp,
      approved,
      blocked,
      pending,
      totalProjects: projects.length,
    };
  }, [employees, empPage, projects]);

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="m-0">Dashboard</h2>
          <small className="text-muted">
            Overview of your security console
          </small>
        </div>
      </div>

      <div className="kpi-grid">
        <Tile label="Total Employees" value={stats.totalEmp} />
        <Tile
          label="Approved"
          value={stats.approved}
          accent="kpi-ok"
          trend="Can log in"
        />
        <Tile
          label="Blocked"
          value={stats.blocked}
          accent="kpi-danger"
          trend="Access revoked"
        />
        <Tile
          label="Pending"
          value={stats.pending}
          accent="kpi-warn"
          trend="Awaiting approval"
        />
        <Tile
          label="Projects"
          value={stats.totalProjects}
          accent="kpi-accent"
        />
      </div>

      <div className="dash-panel">
        <div className="panel-head">
          <h3>Recently added employees</h3>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate("/employees")}
          >
            Manage all
          </button>
        </div>
        <table className="table align-middle mb-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Projects</th>
            </tr>
          </thead>
          <tbody>
            {employees.slice(0, 8).map((e) => (
              <tr key={e._id}>
                <td>
                  {[e.firstname, e.lastname].filter(Boolean).join(" ") || "—"}
                </td>
                <td>{e.email}</td>
                <td>{e.role}</td>
                <td>
                  <span className={`status-badge ${e.status || "approved"}`}>
                    {e.status || "—"}
                  </span>
                </td>
                <td>{(e.projects || []).length}</td>
              </tr>
            ))}
            {employees.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted">
                  No employees yet. Add one from Employees.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="dash-panel">
        <div className="panel-head">
          <h3>Projects</h3>
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => navigate("/projects")}
          >
            Open
          </button>
        </div>
        <table className="table align-middle mb-0">
          <thead>
            <tr>
              <th>Project</th>
              <th>Members</th>
              <th>Web URL</th>
            </tr>
          </thead>
          <tbody>
            {projects.slice(0, 8).map((p) => (
              <tr key={p._id}>
                <td>{p.project_name}</td>
                <td>{(p.members || []).length}</td>
                <td>
                  <a href={p.web_url} target="_blank" rel="noreferrer">
                    {p.web_url}
                  </a>
                </td>
              </tr>
            ))}
            {projects.length === 0 && (
              <tr>
                <td colSpan={3} className="text-center text-muted">
                  No projects yet. Create one from Projects.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Tile({ label, value, accent, trend }) {
  return (
    <div className="kpi-tile">
      <span className="kpi-label">{label}</span>
      <span className={`kpi-value ${accent || ""}`}>{value ?? "—"}</span>
      {trend && <span className="kpi-trend">{trend}</span>}
    </div>
  );
}
